// src/pages/admin/DepositSheet.jsx
import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@api/useAuth";
import { Card, Button, Loading, Modal, SegmentedControl, usePersistedState } from "@common";
import {
  useMultiClinicCollectionBreakdownsForMonths,
  useClinicSubsidiaryMap,
  useSearchNetsuiteTransactionLinesForMonths,
  useSearchNetsuiteDepositsForMonths,
  useCashBalanceDailyForMonths,
  useInsuranceEftDay,
  useMultiClinicInsuranceEftTotalsForMonths,
  useDepositMatchesForClinics,
} from "@api/hooks";
import { roundTo2 } from "@utils/utils";
import { canViewClinicReport, canViewDailyBankBalance } from "@utils/roles";
import { formatMoney } from "@utils/format";
import { classifyMemo } from "@utils/memoFormats";
import { getMonthRange, pad2 } from "@utils/dateRanges";
import {
  DEPOSIT_SHEET_COLUMNS as MONTHLY_TABLE_COLUMNS,
  DEPOSIT_SHEET_COLUMN_COLORS as COLUMN_COLORS,
  DEPOSIT_SHEET_MATCH_KEYS as MATCH_KEYS,
  DEPOSIT_SHEET_MATCH_COL_KEYS as MATCH_COL_KEYS,
  DEPOSIT_SHEET_DERIVED_KEYS as DERIVED_KEYS,
  DEPOSIT_SHEET_TOTAL_ONLY_KEYS as TOTAL_ONLY_KEYS,
  DEPOSIT_SHEET_BANK_FIELDS,
  refundsSummary,
} from "@utils/depositSheetColumns";

// Cells are coloured by ONE reconciliation at a time (picked at the top of the
// page); each has its own colours so a screenshot says which one it shows.
//   netsuite — NetSuite deposits + uploaded insurance PDFs: green / red
//   bank     — deposit lines Accepted against bank lines on Daily Deposits:
//              blue (matched) / amber (not matched yet, or changed since)
// The two Diff. columns always keep their own colours, whichever is picked.
const COLOR_BY_OPTIONS = [
  { value: "netsuite", label: "NetSuite & PDFs" },
  { value: "bank", label: "Bank feeds" },
];
const BANK_FIELD_KEYS = new Set(DEPOSIT_SHEET_BANK_FIELDS.map((c) => c.key));
const BANK_KEYS = new Set(["bank_matched", "bank_difference"]);

// The Bank pair only for roles that can read saved matches. The tables and the
// all-clinics CSV both use this, so their columns line up.
const visibleColumns = (canSeeBank) =>
  canSeeBank ? MONTHLY_TABLE_COLUMNS : MONTHLY_TABLE_COLUMNS.filter((c) => !BANK_KEYS.has(c.key));

const bankStatusClass = (status) =>
  status === "match"
    ? "bg-palette-sky-muted text-palette-sky-primary font-medium"
    : status === "mismatch"
      ? "bg-palette-amber-muted text-palette-amber-primary"
      : "";

/**
 * Saved matches → { "<clinic>|<date>": { <field>: { amount, banks, by } } }, the
 * shape each table row carries as `bankByField`. A deposit line's key is
 * "<date>:<field>", i.e. exactly one Deposit Sheet cell.
 */
function indexBankMatches(byClinic) {
  const out = {};
  Object.entries(byClinic || {}).forEach(([clinic, matches]) => {
    (matches || []).forEach((m) => {
      const banks = m.lines.filter((l) => l.side === "bank");
      m.lines.forEach((l) => {
        if (l.side !== "deposit") return;
        const field = l.key.slice(l.key.indexOf(":") + 1);
        const rowKey = `${clinic}|${l.date}`;
        out[rowKey] = out[rowKey] || {};
        out[rowKey][field] = {
          amount: Number(l.amount) || 0,
          banks,
          by: m.created_by_name,
          at: m.created_at,
        };
      });
    });
  });
  return out;
}

/** One bank-bound cell against its saved match (if any): { status, title }. */
function bankCellMatch(amountValue, line) {
  const amount = roundTo2(Number(amountValue) || 0);
  if (!line) {
    return amount === 0
      ? { status: "match", title: "Nothing to reconcile" }
      : { status: "mismatch", title: "Not matched to a bank line yet (Daily Deposits)" };
  }
  if (roundTo2(line.amount) !== amount) {
    return {
      status: "mismatch",
      title:
        `Accepted at ${formatMoney(line.amount)}, but the Collection Breakdown now says ` +
        `${formatMoney(amount)}. Unmatch and match again on Daily Deposits.`,
    };
  }
  const banks = line.banks.map((b) => `${b.date} ${b.description} ${formatMoney(b.amount)}`);
  return {
    status: "match",
    title: [`Matched to bank${line.by ? ` by ${line.by}` : ""}:`, ...banks].join("\n"),
  };
}

function bankDifferenceTitle(cmp) {
  if (!cmp) return "Bank matches not loaded";
  const both = `Bank-bound ${formatMoney(cmp.expected)} − Bank Matched ${formatMoney(cmp.matched)}`;
  if (cmp.status === "match") return `${both}. Fully matched to the bank.`;
  if (cmp.status === "mismatch") return `${both}. Not all matched to the bank yet.`;
  return both;
}

const bankVerdict = (expected, matched) =>
  expected === 0 && matched === 0
    ? "neutral"
    : roundTo2(expected - matched) === 0
      ? "match"
      : "mismatch";

/**
 * Hover text for a Difference cell.
 *
 * Earns its place on the refund case: the ± refund tolerance can call a day
 * matched while the raw difference is the refund amount, so a GREEN cell can show
 * a non-zero number. Without this it reads as a contradiction.
 */
function differenceTitle(cmp) {
  if (!cmp || cmp.netsuiteTotal === null) return "No NetSuite deposit lines for this day";
  const both = `Deposit Total ${formatMoney(cmp.expected)} − NetSuite Total ${formatMoney(
    cmp.netsuiteTotal,
  )}`;
  if (cmp.status === "match" && cmp.difference !== 0) {
    return `${both}. Treated as matched: the gap equals the day's refund of ${formatMoney(
      cmp.refund,
    )}.`;
  }
  if (cmp.status === "mismatch") return `${both}. Does not reconcile.`;
  if (cmp.status === "match") return `${both}. Reconciles exactly.`;
  return both;
}

// Card kinds we expose as per-tx buckets (visa/mastercard/amex/debit). Any
// memo that doesn't classify as one of these falls into "other".
const CARD_BUCKET_KINDS = new Set(["visa", "mastercard", "amex", "debit"]);

/**
 * Given a NetSuite transaction (with `lines: [{memo, amount, ...}]`),
 * return a new object that adds per-card-type totals derived from the
 * line memos via `classifyMemo`. Also annotates each line with `kind`.
 */
function enrichTxWithCardBuckets(tx) {
  const buckets = { visa: 0, mastercard: 0, amex: 0, debit: 0, other: 0 };
  const lines = (tx?.lines || []).map((ln) => {
    const kind = classifyMemo(ln?.memo);
    const amt = Number(ln?.amount) || 0;
    const bucket = CARD_BUCKET_KINDS.has(kind) ? kind : "other";
    buckets[bucket] += amt;
    return { ...ln, kind };
  });
  return { ...tx, ...buckets, lines };
}

// Compare a day's NetSuite Deposit lines against the deposit-sheet record.
// Memos classified as visa/mastercard/amex/debit compare against the matching
// card column. "moneris" memos compare against the sum of all four card
// columns. Returns null when there's no record / no usable lines.
function depositMatchForDay(record, depositLines) {
  if (!record || !depositLines || depositLines.length === 0) return null;
  const refund = roundTo2(Number(record?.refund) || 0);
  const targets = {
    visa: roundTo2(Number(record.collection_visa) || 0),
    mastercard: roundTo2(Number(record.collection_mastercard) || 0),
    amex: roundTo2(Number(record.collection_amex) || 0),
    debit: roundTo2(Number(record.collection_debit) || 0),
  };
  const cardsTotal = roundTo2(targets.visa + targets.mastercard + targets.amex + targets.debit);
  let positives = 0;
  let mismatches = 0;
  const annotated = depositLines.map((ln) => {
    const kind = ln.kind || classifyMemo(ln?.memo);
    const amt = roundTo2(Number(ln?.amount) || 0);
    let target = null;
    if (kind === "moneris") target = cardsTotal;
    else if (kind && kind in targets) target = targets[kind];

    let status = "neutral";
    if (target !== null && amt !== 0 && target !== 0) {
      if (
        amt === target ||
        (refund !== 0 && (roundTo2(target + refund) === amt || roundTo2(target - refund) === amt))
      ) {
        status = "match";
        positives++;
      } else {
        status = "mismatch";
        mismatches++;
      }
    }
    return { ...ln, kind, target, status };
  });
  return {
    lines: annotated,
    positives,
    mismatches,
    ok: positives > 0 && mismatches === 0,
  };
}

// Pick the best-matching transaction for a single deposit-sheet record.
//
// Per card-type column, the cell falls into one of three states:
//   - "match"    : both sides non-zero and equal (rounded to 2 decimals)
//   - "mismatch" : both sides non-zero but different
//   - "neutral"  : at least one side is 0 (no data on that side — treated
//                  as still correct so we don't paint it red)
//
// `count` is the number of real positive matches and drives best-tx
// selection. We keep a candidate as long as it has at least one match OR
// zero mismatches (i.e. nothing contradicts it).
function bestTxMatch(record, transactions) {
  if (!record || !transactions || transactions.length === 0) return null;
  const refund = roundTo2(Number(record?.refund) || 0);
  let best = null;
  for (const tx of transactions) {
    let positives = 0;
    let neutrals = 0;
    let mismatches = 0;
    const flags = {};
    for (const { col, tx: txKey } of MATCH_KEYS) {
      const a = roundTo2(Number(record[col]) || 0);
      const b = roundTo2(Number(tx[txKey]) || 0);
      if (a === 0 || b === 0) {
        flags[col] = "neutral";
        neutrals++;
      } else if (
        a === b ||
        (refund !== 0 && (roundTo2(a + refund) === b || roundTo2(a - refund) === b))
      ) {
        // Direct match, or matches once refund is added/subtracted from
        // the deposit-sheet side (refunds reduce the net card debit
        // posted to NetSuite).
        flags[col] = "match";
        positives++;
      } else {
        flags[col] = "mismatch";
        mismatches++;
      }
    }
    if (positives === 0) continue;
    const count = positives + neutrals;
    if (!best || count > best.count || (count === best.count && mismatches < best.mismatches)) {
      best = { tx, count, positives, neutrals, mismatches, flags };
    }
  }
  return best;
}

const EMPTY_EFT_TOTALS = {};

// The two amounts read from uploaded files, and where each lives in the API and
// on the Collection Breakdown record. One table / cell rule serves both.
const PAYMENT_KINDS = {
  eft: {
    title: "Insurance EFTs",
    noun: "insurance EFTs",
    rowsKey: "eft_payments",
    countedKey: "counted",
    statusKey: "status",
    totalKey: "total_amount",
    reviewKey: "review_reason",
    dayTotalKey: "total",
    recordField: "collection_insurance_eft",
    enteredLabel: "Collection Breakdown Insurance EFT",
  },
  cheque: {
    title: "Insurance Cheques",
    noun: "insurance cheques",
    rowsKey: "cheque_payments",
    countedKey: "cheque_counted",
    statusKey: "cheque_status",
    totalKey: "cheque_total",
    reviewKey: "cheque_review_reason",
    dayTotalKey: "cheque_total",
    recordField: "collection_insurance_check",
    enteredLabel: "Collection Breakdown Insurance check",
  },
};

/**
 * Entered amount vs the day's total read from uploaded files, for one kind
 * (insurance EFTs or cheques). Equal to the cent is a match and different is a
 * mismatch — including an amount in the files with $0 entered (not entered
 * yet). Neutral only when the files have no reading for the day or both are $0.
 * Used by both the table cell and the NetSuite match modal so they cannot
 * disagree.
 */
function readAmountMatch(record, eftDay, kind = "eft") {
  const { recordField, dayTotalKey } = PAYMENT_KINDS[kind];
  const entered = roundTo2(Number(record?.[recordField]) || 0);
  const dayTotal = eftDay?.[dayTotalKey];
  const fromFiles = dayTotal != null ? roundTo2(Number(dayTotal) || 0) : null;
  if (fromFiles === null) {
    return {
      status: "neutral",
      title: eftDay?.pending ? "Uploaded files are still being read" : undefined,
    };
  }
  const both = `Entered ${formatMoney(entered)} · Uploaded files ${formatMoney(fromFiles)}`;
  const review = eftDay?.needs_review ? " (check files)" : "";
  // Unlike the card columns, a $0 here is not "no data": the files were read. So
  // an amount in the files with nothing entered is red (not entered yet), and
  // both $0 is simply nothing to compare.
  if (entered === 0 && fromFiles === 0) return { status: "neutral", title: both + review };
  if (entered === 0) {
    return {
      status: "mismatch",
      title: `${both}. Not entered on the Collection Breakdown.${review}`,
    };
  }
  return entered === fromFiles
    ? { status: "match", title: `${both}. Matches.${review}` }
    : { status: "mismatch", title: `${both}. Does not match.${review}` };
}

function downloadCsvLines(lines, filename) {
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * Deposit-sheet table. Each row is one clinic-day:
 *   { key, clinic, date, record, transactions, depositLines, eftTotals }
 * `mode` "day" lists one clinic's days (first column = Day); "clinic" lists one
 * day across clinics (first column = Clinic). Rows are built by the parent so all
 * clinics share the same underlying API requests.
 */
function DepositTable({
  rows,
  mode = "day",
  title,
  heading,
  subtitle,
  csvName,
  emptyText,
  isLoading,
  // Page-level "Download CSV — All Clinics": called with (key, build), where
  // build() returns { rows, totals } as arrays of MONTHLY_TABLE_COLUMNS values.
  // Returns the unregister function.
  onRegisterCsvExport,
  csvExportKey,
  colorBy = "netsuite",
  canSeeBank = false,
  // Saved matches loaded / failed; rows carry them as `bankByField`.
  bankLoaded = false,
  bankError = false,
}) {
  const firstColLabel = mode === "clinic" ? "Clinic" : "Day";
  const columns = useMemo(() => visibleColumns(canSeeBank), [canSeeBank]);
  const [activeMatch, setActiveMatch] = useState(null);
  const [activeBank, setActiveBank] = useState(null);
  // First column links each day to that clinic's Daily Financial Reporting. Only
  // for roles the /report/:clinicId route admits (not Payroll / Finance Coord).
  const { user } = useAuth();
  const showReportLink = canViewClinicReport(user);
  const [cashBalanceExpanded, setCashBalanceExpanded] = useState(false);

  const balanceClinics = useMemo(() => Array.from(new Set(rows.map((r) => r.clinic))), [rows]);
  const balanceMonths = useMemo(() => {
    const seen = new Map();
    rows.forEach(({ date }) => {
      const ym = date.slice(0, 7);
      if (!seen.has(ym)) seen.set(ym, { year: Number(ym.slice(0, 4)), month: Number(ym.slice(5)) });
    });
    return Array.from(seen.values());
  }, [rows]);
  const { data: dailyBalanceData, isLoading: isDailyBalanceLoading } = useCashBalanceDailyForMonths(
    { clinics: balanceClinics, months: balanceMonths },
    { enabled: cashBalanceExpanded && rows.length > 0 },
  );

  // Keyed by row key (`clinic|date`).
  const dailyBalanceByKey = useMemo(() => {
    const map = {};
    Object.entries(dailyBalanceData?.byClinic || {}).forEach(([clinic, days]) => {
      days.forEach(({ date, balance, cash_in, cash_out }) => {
        map[`${clinic}|${date}`] = {
          balance,
          delta: (Number(cash_in) || 0) - (Number(cash_out) || 0),
        };
      });
    });
    return map;
  }, [dailyBalanceData]);

  const monthlyRows = rows;

  // Sums of the RECORD-backed fields. Derived columns are absent here by design —
  // a difference is not a sum, so the footer computes those from these totals
  // instead (see `monthlyDerivedTotals`).
  //
  // `TOTAL_ONLY_KEYS` carries `collection_total`, which no column displays any
  // more but which `deposit_total` is derived from, so its sum is still needed.
  const monthlyTotals = useMemo(() => {
    const keys = [
      ...MONTHLY_TABLE_COLUMNS.filter((c) => !DERIVED_KEYS.has(c.key)).map((c) => c.key),
      ...TOTAL_ONLY_KEYS,
    ];
    const totals = {};
    keys.forEach((key) => {
      totals[key] = 0;
    });
    monthlyRows.forEach(({ record }) => {
      if (!record) return;
      keys.forEach((key) => {
        const n = Number(record[key]);
        if (Number.isFinite(n)) totals[key] += n;
      });
    });
    return totals;
  }, [monthlyRows]);

  // Per-row best NetSuite transaction match (by clinic subsidiary). Only
  // transactions whose `trandate` equals the row's day are considered.
  const matchByDate = useMemo(() => {
    const m = {};
    monthlyRows.forEach(({ key, date: iso, record, transactions }) => {
      if (!record) return;
      const sameDay = (transactions || []).filter((tx) => tx?.trandate === iso);
      if (sameDay.length === 0) return;
      const best = bestTxMatch(record, sameDay);
      if (best) m[key] = best;
    });
    return m;
  }, [monthlyRows]);

  // Each row's NetSuite deposit lines for its clinic-day, by row key.
  const depositLinesByDate = useMemo(() => {
    const m = {};
    monthlyRows.forEach(({ key, depositLines }) => {
      if (depositLines) m[key] = depositLines;
    });
    return m;
  }, [monthlyRows]);

  // Each row's intercompany total, by row key. Absent on days with none, so the
  // cell reads "—" rather than $0.00.
  const intercompanyByDate = useMemo(() => {
    const m = {};
    monthlyRows.forEach(({ key, intercompanyLines }) => {
      if (!intercompanyLines || intercompanyLines.length === 0) return;
      m[key] = roundTo2(intercompanyLines.reduce((sum, ln) => sum + (Number(ln?.amount) || 0), 0));
    });
    return m;
  }, [monthlyRows]);

  // Per-row deposit match: compare each deposit line for that day against the
  // record's card columns (or sum of 4 columns for moneris memos).
  const depositMatchByDate = useMemo(() => {
    const m = {};
    monthlyRows.forEach(({ key, record }) => {
      if (!record) return;
      const lines = depositLinesByDate[key];
      if (!lines || lines.length === 0) return;
      const r = depositMatchForDay(record, lines);
      if (r) m[key] = r;
    });
    return m;
  }, [monthlyRows, depositLinesByDate]);

  // Per-row aggregate compare, and the single source for the three
  // reconciliation columns (Deposit Total / NetSuite Total / Difference).
  //
  // `expected` is the row's collection total MINUS cash: cash is not part of an
  // electronic deposit, so it is the only figure comparable to what NetSuite
  // received. `netsuiteTotal` is the same number the NetSuite-match modal shows
  // in its footer — both sum that day's deposit lines.
  //
  // Unlike the card-column matching, an entry is produced for every row that has
  // a record, even with no NetSuite lines: Deposit Total is still a real value
  // then, while NetSuite Total and Difference render as "—".
  const depositTotalStatusByDate = useMemo(() => {
    const m = {};
    monthlyRows.forEach(({ key, record }) => {
      if (!record) return;
      const lines = depositLinesByDate[key];
      const hasLines = Array.isArray(lines) && lines.length > 0;
      const netsuiteTotal = hasLines
        ? roundTo2(lines.reduce((sum, ln) => sum + (Number(ln?.amount) || 0), 0))
        : null;
      const expected = roundTo2(
        (Number(record.collection_total) || 0) - (Number(record.collection_cash) || 0),
      );
      const refund = roundTo2(Number(record?.refund) || 0);
      let status = "neutral";
      if (netsuiteTotal !== null && netsuiteTotal !== 0 && expected !== 0) {
        if (
          netsuiteTotal === expected ||
          (refund !== 0 &&
            (roundTo2(expected + refund) === netsuiteTotal ||
              roundTo2(expected - refund) === netsuiteTotal))
        ) {
          status = "match";
        } else {
          status = "mismatch";
        }
      }
      m[key] = {
        status,
        netsuiteTotal,
        expected,
        refund,
        difference: netsuiteTotal === null ? null : roundTo2(expected - netsuiteTotal),
      };
    });
    return m;
  }, [monthlyRows, depositLinesByDate]);

  // Per-row bank pair: how much of the day's bank-bound money is matched.
  const bankStatusByKey = useMemo(() => {
    const m = {};
    if (!bankLoaded) return m;
    monthlyRows.forEach(({ key, record, bankByField }) => {
      if (!record) return;
      const expected = roundTo2(
        DEPOSIT_SHEET_BANK_FIELDS.reduce((sum, f) => sum + (Number(record[f.key]) || 0), 0),
      );
      const matched = roundTo2(
        DEPOSIT_SHEET_BANK_FIELDS.reduce((sum, f) => sum + (bankByField?.[f.key]?.amount || 0), 0),
      );
      m[key] = {
        expected,
        matched,
        difference: roundTo2(expected - matched),
        status: bankVerdict(expected, matched),
      };
    });
    return m;
  }, [bankLoaded, monthlyRows]);

  // Value for one column of one row. Derived columns are not fields on the
  // collection-breakdown record, so the table body, the footer and the CSV export
  // all resolve through here rather than each re-deriving the arithmetic.
  const cellValue = useCallback(
    (record, key, rowKey) => {
      if (!DERIVED_KEYS.has(key)) return record ? record[key] : null;
      if (key === "intercompany") return intercompanyByDate[rowKey] ?? null;
      if (BANK_KEYS.has(key)) {
        const bank = bankStatusByKey[rowKey];
        if (!bank) return null;
        if (key === "bank_matched") return bank.matched || null;
        return bank.difference;
      }
      const cmp = depositTotalStatusByDate[rowKey];
      if (!cmp) return null;
      if (key === "deposit_total") return cmp.expected;
      if (key === "netsuite_total") return cmp.netsuiteTotal;
      return cmp.difference;
    },
    [depositTotalStatusByDate, intercompanyByDate, bankStatusByKey],
  );

  // Month-level counterparts of the three derived columns, plus the same
  // match/mismatch verdict applied to the whole month. The footer and the CSV
  // export both read this, so the column total and the tint cannot disagree.
  const monthlyDerivedTotals = useMemo(() => {
    const expected = roundTo2(
      (monthlyTotals.collection_total || 0) - (monthlyTotals.collection_cash || 0),
    );
    const netsuiteTotal = roundTo2(
      monthlyRows.reduce((sum, { key }) => {
        const lines = depositLinesByDate[key] || [];
        return sum + lines.reduce((s, ln) => s + (Number(ln?.amount) || 0), 0);
      }, 0),
    );
    const refund = roundTo2(monthlyTotals.refund || 0);
    const bankDays = Object.values(bankStatusByKey);
    const bankExpected = roundTo2(bankDays.reduce((sum, b) => sum + b.expected, 0));
    const bankMatched = roundTo2(bankDays.reduce((sum, b) => sum + b.matched, 0));
    let status = "neutral";
    if (netsuiteTotal !== 0 && expected !== 0) {
      status =
        netsuiteTotal === expected ||
        (refund !== 0 &&
          (roundTo2(expected + refund) === netsuiteTotal ||
            roundTo2(expected - refund) === netsuiteTotal))
          ? "match"
          : "mismatch";
    }
    return {
      status,
      deposit_total: expected,
      netsuite_total: netsuiteTotal,
      deposit_difference: roundTo2(expected - netsuiteTotal),
      intercompany: roundTo2(Object.values(intercompanyByDate).reduce((sum, v) => sum + v, 0)),
      bank_matched: bankMatched,
      bank_difference: roundTo2(bankExpected - bankMatched),
      bank: bankLoaded
        ? {
            expected: bankExpected,
            matched: bankMatched,
            status: bankVerdict(bankExpected, bankMatched),
          }
        : null,
    };
  }, [
    monthlyTotals,
    monthlyRows,
    depositLinesByDate,
    intercompanyByDate,
    bankStatusByKey,
    bankLoaded,
  ]);

  // Footer value for any column, derived or not.
  const footerValue = useCallback(
    (key) => (DERIVED_KEYS.has(key) ? monthlyDerivedTotals[key] : monthlyTotals[key] || 0),
    [monthlyDerivedTotals, monthlyTotals],
  );

  // Green/red/neutral for a reconciliation cell, matching the modal's footer and
  // the ± refund tolerance used everywhere else on this sheet.
  const statusClass = (status) =>
    status === "match"
      ? "bg-palette-emerald-muted text-palette-emerald-primary font-medium"
      : status === "mismatch"
        ? "bg-palette-rose-muted text-palette-rose-primary"
        : "";

  function handleDownloadCsv() {
    if (rows.length === 0) return;
    const header = [
      firstColLabel,
      ...columns.flatMap((c) =>
        c.key === "collection_cash" && cashBalanceExpanded
          ? [c.label, "Cash Balance", "Change"]
          : [c.label],
      ),
    ];
    const lines = [header.map(csvCell).join(",")];
    monthlyRows.forEach(({ key, clinic, date: iso, record }) => {
      const row = [mode === "clinic" ? clinic : iso];
      columns.forEach((c) => {
        const v = cellValue(record, c.key, key);
        row.push(v === null || v === undefined || v === "" ? "" : Number(v));
        if (c.key === "collection_cash" && cashBalanceExpanded) {
          const entry = dailyBalanceByKey[key];
          row.push(entry?.balance != null ? Number(entry.balance) : "");
          row.push(entry?.delta != null ? entry.delta : "");
        }
      });
      lines.push(row.map(csvCell).join(","));
    });
    const totalsRow = ["Total"];
    columns.forEach((c) => {
      totalsRow.push(roundTo2(footerValue(c.key) || 0));
      if (c.key === "collection_cash" && cashBalanceExpanded) {
        // Day mode: the last day's balance. Clinic mode: all clinics' balances summed.
        const entries =
          mode === "clinic"
            ? monthlyRows.map((r) => dailyBalanceByKey[r.key])
            : [dailyBalanceByKey[monthlyRows[monthlyRows.length - 1]?.key]];
        const known = entries.filter((e) => e?.balance != null);
        totalsRow.push(
          known.length ? roundTo2(known.reduce((sum, e) => sum + Number(e.balance), 0)) : "",
        );
        totalsRow.push("");
      }
    });
    lines.push(totalsRow.map(csvCell).join(","));

    downloadCsvLines(lines, csvName);
  }

  // Same figures as the per-table CSV, without the optional Cash Balance
  // columns: those depend on each table's own expand state, and one combined
  // file needs one set of headers.
  useEffect(() => {
    if (!onRegisterCsvExport || !csvExportKey) return undefined;
    const build = () => ({
      rows: monthlyRows.map(({ key, date: iso, record }) => [
        iso,
        ...columns.map((c) => {
          const v = cellValue(record, c.key, key);
          return v === null || v === undefined || v === "" ? "" : Number(v);
        }),
      ]),
      totals: columns.map((c) => roundTo2(footerValue(c.key) || 0)),
    });
    return onRegisterCsvExport(csvExportKey, build);
  }, [onRegisterCsvExport, csvExportKey, monthlyRows, columns, cellValue, footerValue]);

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <h3 className="text-lg font-semibold">{title}</h3>
          <h4 className="text-base font-medium mt-1">{heading}</h4>
          <p className="text-xs text-palette-gray-secondary">{subtitle}</p>
        </div>
        <Button
          type="button"
          variant="secondary"
          onClick={handleDownloadCsv}
          disabled={isLoading || monthlyRows.length === 0}
        >
          Download CSV
        </Button>
      </div>

      <div className="overflow-auto border border-palette-gray-divider rounded max-h-[32rem]">
        <table className="min-w-full text-sm">
          <thead className="bg-palette-gray-soft text-palette-gray-heading sticky top-0 z-10">
            <tr>
              {showReportLink && (
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Report</th>
              )}
              <th className="px-3 py-2 text-left font-medium whitespace-nowrap">{firstColLabel}</th>
              {columns.flatMap((c) => {
                const color = COLUMN_COLORS[c.key] || COLUMN_COLORS.collection_other;
                const th = (
                  <th
                    key={c.key}
                    title={c.hint || undefined}
                    className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${color.soft}`}
                  >
                    {c.key === "collection_cash" ? (
                      <span className="inline-flex items-center gap-1.5">
                        {c.label}
                        <button
                          type="button"
                          onClick={() => setCashBalanceExpanded((v) => !v)}
                          title={cashBalanceExpanded ? "Hide cash balance" : "Show cash balance"}
                          className="rounded px-1 py-0.5 text-xs text-palette-emerald-hover hover:bg-palette-emerald-border transition-colors"
                        >
                          {cashBalanceExpanded ? "◂" : "▸"}
                        </button>
                      </span>
                    ) : c.hint ? (
                      // The three reconciliation columns carry a one-line
                      // definition under the label — "Total − Cash" is the whole
                      // reason the numbers reconcile, and it should not be
                      // hover-only.
                      <span className="flex flex-col items-end leading-tight">
                        {c.label}
                        <span className="text-[10px] font-normal opacity-70">{c.hint}</span>
                      </span>
                    ) : (
                      c.label
                    )}
                  </th>
                );
                if (c.key === "collection_cash" && cashBalanceExpanded) {
                  return [
                    th,
                    <th
                      key="cash_balance_head"
                      className="px-3 py-2 text-right font-semibold whitespace-nowrap bg-palette-emerald-soft text-palette-emerald-text"
                    >
                      Cash Balance
                    </th>,
                    <th
                      key="cash_change_head"
                      className="px-3 py-2 text-right font-semibold whitespace-nowrap bg-palette-emerald-soft text-palette-emerald-hover"
                    >
                      Change
                    </th>,
                  ];
                }
                return [th];
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={
                    columns.length + 1 + (showReportLink ? 1 : 0) + (cashBalanceExpanded ? 2 : 0)
                  }
                  className="px-3 py-4 text-center text-palette-gray-secondary"
                >
                  Loading…
                </td>
              </tr>
            ) : monthlyRows.every((r) => !r.record) ? (
              <tr>
                <td
                  colSpan={
                    columns.length + 1 + (showReportLink ? 1 : 0) + (cashBalanceExpanded ? 2 : 0)
                  }
                  className="px-3 py-6 text-center text-palette-gray-secondary italic"
                >
                  {emptyText}
                </td>
              </tr>
            ) : (
              monthlyRows.map(({ key, clinic, date: iso, record, eftTotals, bankByField }) => {
                const match = matchByDate[key];
                const depositMatch = depositMatchByDate[key];
                // Any day with a Collection Breakdown opens the modal, not only
                // NetSuite-matched ones: its Insurance EFTs section comes from the
                // day's uploaded files, which exist whether or not NetSuite does.
                const clickable = !!(match || depositMatch || record);
                return (
                  <tr
                    key={key}
                    className={`border-t border-palette-gray-subtle ${
                      clickable ? "cursor-pointer hover:bg-palette-gray-soft" : ""
                    }`}
                    onClick={
                      clickable
                        ? () => setActiveMatch({ clinic, date: iso, record, match, depositMatch })
                        : undefined
                    }
                  >
                    {showReportLink && (
                      <td className="px-3 py-2 whitespace-nowrap">
                        <Link
                          to={`/report/${encodeURIComponent(clinic)}?tab=financial&date=${iso}`}
                          // The row itself opens the NetSuite match modal.
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-medium text-palette-indigo-badge hover:text-palette-indigo-heading hover:underline"
                          title={`Open ${clinic} Daily Financial Reporting for ${iso}`}
                        >
                          Open ↗
                        </Link>
                      </td>
                    )}
                    <td
                      className={`px-3 py-2 whitespace-nowrap ${mode === "clinic" ? "font-semibold" : ""}`}
                    >
                      {mode === "clinic" ? clinic : iso}
                    </td>
                    {columns.flatMap((c) => {
                      const v = cellValue(record, c.key, key);
                      const isCardCol = MATCH_COL_KEYS.has(c.key);
                      const byNetsuite = colorBy !== "bank";
                      let cellClass = "";
                      if (byNetsuite && match && isCardCol) {
                        const flag = match.flags[c.key];
                        if (flag === "mismatch") {
                          cellClass = "bg-palette-rose-muted text-palette-rose-primary";
                        } else {
                          cellClass =
                            "bg-palette-emerald-muted text-palette-emerald-primary font-medium";
                        }
                      }
                      // The verdict now rides on Difference, which is where it
                      // actually reads: it was on the old raw Total cell, whose
                      // number was never the one being compared.
                      let cellTitle;
                      if (c.key === "deposit_difference") {
                        const cmp = depositTotalStatusByDate[key];
                        cellClass = statusClass(cmp?.status);
                        cellTitle = differenceTitle(cmp);
                      }
                      const readKind =
                        c.key === "collection_insurance_eft"
                          ? "eft"
                          : c.key === "collection_insurance_check"
                            ? "cheque"
                            : null;
                      if (byNetsuite && readKind) {
                        const read = readAmountMatch(record, eftTotals, readKind);
                        cellClass = statusClass(read.status);
                        cellTitle = read.title;
                      }
                      // An empty / $0.00 cell on a day that has a breakdown has
                      // nothing to reconcile, so it reads as fine (green) and the
                      // eye only stops on red. Never overrides a status already set
                      // (red stays red), never on days without a breakdown (those
                      // are missing, not fine), and not on the computed columns,
                      // where "—" means "no NetSuite data".
                      const isEmpty = v === null || v === undefined || v === "" || Number(v) === 0;
                      if (
                        byNetsuite &&
                        record &&
                        isEmpty &&
                        !cellClass &&
                        !DERIVED_KEYS.has(c.key)
                      ) {
                        cellClass = statusClass("match");
                        cellTitle = cellTitle || "Nothing to reconcile";
                      }
                      // Bank mode: only bank-bound fields are coloured; Cash,
                      // Refund and Other never reach the bank.
                      if (!byNetsuite && record && BANK_FIELD_KEYS.has(c.key)) {
                        if (bankLoaded) {
                          const b = bankCellMatch(v, bankByField?.[c.key]);
                          cellClass = bankStatusClass(b.status);
                          cellTitle = b.title;
                        } else {
                          cellTitle = bankError
                            ? "Bank matches could not be loaded"
                            : "Loading bank matches…";
                        }
                      }
                      // Bank Matched carries the same verdict as Bank Diff., and
                      // opens the day's bank matches instead of the NetSuite modal.
                      const bank = BANK_KEYS.has(c.key) ? bankStatusByKey[key] : null;
                      if (BANK_KEYS.has(c.key)) {
                        cellClass = bankStatusClass(bank?.status);
                        cellTitle = bankDifferenceTitle(bank);
                      }
                      const opensBank = c.key === "bank_matched" && !!bank;
                      const td = (
                        <td
                          key={c.key}
                          title={cellTitle}
                          className={`px-3 py-2 text-right whitespace-nowrap ${cellClass} ${
                            opensBank ? "underline decoration-dotted underline-offset-2" : ""
                          }`}
                          onClick={
                            opensBank
                              ? (e) => {
                                  e.stopPropagation();
                                  setActiveBank({ clinic, date: iso, record, bankByField, bank });
                                }
                              : undefined
                          }
                        >
                          {v === null || v === undefined || v === "" ? (
                            <span className="text-palette-gray-border">—</span>
                          ) : (
                            formatMoney(Number(v))
                          )}
                          {c.key === "refund" && refundsSummary(record) && (
                            <span className="block text-[11px] text-palette-gray-secondary">
                              {refundsSummary(record)}
                            </span>
                          )}
                        </td>
                      );
                      if (c.key === "collection_cash" && cashBalanceExpanded) {
                        const entry = dailyBalanceByKey[key];
                        const bal = entry?.balance;
                        const delta = entry?.delta;
                        return [
                          td,
                          <td
                            key={`${key}_bal`}
                            className="px-3 py-2 text-right whitespace-nowrap tabular-nums text-palette-emerald-text"
                          >
                            {isDailyBalanceLoading ? (
                              <span className="text-palette-gray-placeholder">…</span>
                            ) : bal != null ? (
                              formatMoney(Number(bal))
                            ) : (
                              <span className="text-palette-gray-border">—</span>
                            )}
                          </td>,
                          <td
                            key={`${key}_delta`}
                            className="px-3 py-2 text-right whitespace-nowrap tabular-nums"
                          >
                            {isDailyBalanceLoading ? (
                              <span className="text-palette-gray-placeholder">…</span>
                            ) : delta != null ? (
                              <span
                                className={
                                  delta > 0
                                    ? "text-palette-emerald-hover font-medium"
                                    : delta < 0
                                      ? "text-palette-rose-badge font-medium"
                                      : "text-palette-gray-placeholder"
                                }
                              >
                                {delta >= 0 ? "+" : ""}
                                {formatMoney(delta)}
                              </span>
                            ) : (
                              <span className="text-palette-gray-border">—</span>
                            )}
                          </td>,
                        ];
                      }
                      return [td];
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
          <tfoot className="bg-palette-gray-soft font-semibold sticky bottom-0 z-10">
            <tr className="border-t border-palette-gray-divider">
              {showReportLink && <td className="px-3 py-2" />}
              <td className="px-3 py-2 whitespace-nowrap">Total</td>
              {columns.flatMap((c) => {
                const color = COLUMN_COLORS[c.key] || COLUMN_COLORS.collection_other;
                let cellClass = color.soft;
                // Same rule as the row cells, one column over: the month's verdict
                // belongs on the month's Difference.
                let footTitle;
                if (BANK_KEYS.has(c.key)) {
                  cellClass = bankStatusClass(monthlyDerivedTotals.bank?.status) || cellClass;
                  footTitle = bankDifferenceTitle(monthlyDerivedTotals.bank);
                }
                if (c.key === "deposit_difference") {
                  cellClass = statusClass(monthlyDerivedTotals.status) || cellClass;
                  footTitle = differenceTitle({
                    ...monthlyDerivedTotals,
                    expected: monthlyDerivedTotals.deposit_total,
                    netsuiteTotal: monthlyDerivedTotals.netsuite_total,
                    difference: monthlyDerivedTotals.deposit_difference,
                    refund: roundTo2(monthlyTotals.refund || 0),
                  });
                }
                const td = (
                  <td
                    key={c.key}
                    title={footTitle}
                    className={`px-3 py-2 text-right whitespace-nowrap ${cellClass}`}
                  >
                    {formatMoney(footerValue(c.key) || 0)}
                  </td>
                );
                if (c.key === "collection_cash" && cashBalanceExpanded) {
                  return [
                    td,
                    <td key="cash_balance_foot" className="px-3 py-2 bg-palette-emerald-soft" />,
                    <td key="cash_change_foot" className="px-3 py-2 bg-palette-emerald-soft" />,
                  ];
                }
                return [td];
              })}
            </tr>
          </tfoot>
        </table>
      </div>
      <NetsuiteMatchModal
        isOpen={!!activeMatch}
        onClose={() => setActiveMatch(null)}
        clinic={activeMatch?.clinic}
        info={activeMatch}
      />
      <BankMatchModal isOpen={!!activeBank} onClose={() => setActiveBank(null)} info={activeBank} />
    </Card>
  );
}

/**
 * One clinic-day's bank-bound fields against what Daily Deposits Accepted for
 * them: entered amount, matched amount and the bank line(s) behind each match.
 * Rows and colours come from `bankCellMatch`, so they agree with the table.
 */
function BankMatchModal({ isOpen, onClose, info }) {
  const rows = DEPOSIT_SHEET_BANK_FIELDS.map((f) => {
    const entered = roundTo2(Number(info?.record?.[f.key]) || 0);
    const line = info?.bankByField?.[f.key];
    return { ...f, entered, line, ...bankCellMatch(entered, line) };
  }).filter((r) => r.entered !== 0 || r.line);
  const bank = info?.bank;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span>
          Bank match{" "}
          <span className="text-sm font-normal text-palette-gray-secondary">
            {info?.clinic} — {info?.date}
          </span>
        </span>
      }
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {rows.length === 0 ? (
        <p className="text-sm text-palette-gray-secondary italic">
          Nothing on this day goes to the bank.
        </p>
      ) : (
        <div className="overflow-auto border border-palette-gray-divider rounded max-h-[28rem]">
          <table className="min-w-full text-sm">
            <thead className="bg-palette-gray-soft text-palette-gray-heading sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Field</th>
                <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Entered</th>
                <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Matched</th>
                <th className="px-3 py-2 text-left font-medium">Bank line</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Matched by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.key}
                  title={r.title}
                  className={`border-t border-palette-gray-subtle align-top ${bankStatusClass(
                    r.status,
                  )}`}
                >
                  <td className="px-3 py-2 whitespace-nowrap">{r.label}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatMoney(r.entered)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {r.line ? formatMoney(r.line.amount) : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {r.line ? (
                      <div className="flex flex-col gap-0.5">
                        {r.line.banks.map((b, i) => (
                          <span key={i}>
                            <span className="tabular-nums">{b.date}</span> {b.description}{" "}
                            <span className="tabular-nums">
                              {formatMoney(Number(b.amount) || 0)}
                            </span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="italic">Not matched yet (Daily Deposits)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.line?.by || "—"}
                    {r.line?.at && (
                      <span className="block text-xs opacity-70">{r.line.at.slice(0, 10)}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {bank && (
              <tfoot
                className={`font-semibold ${bankStatusClass(bank.status) || "bg-palette-gray-soft"}`}
              >
                <tr className="border-t border-palette-gray-divider">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatMoney(bank.expected)}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatMoney(bank.matched)}
                  </td>
                  <td className="px-3 py-2" colSpan={2}>
                    Difference <span className="tabular-nums">{formatMoney(bank.difference)}</span>
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </Modal>
  );
}

const EFT_IN_PROGRESS_STATUSES = new Set(["pending", "scanning"]);

/**
 * Insurance EFTs or insurance cheques (`kind`) read from the day's uploaded files
 * (backend apps/insurance_eft), one row per payment, totalled and compared with
 * the amount entered on that day's Collection Breakdown. Fetched only while the
 * modal is open; both sections share one cached request for the clinic-day.
 */
function InsurancePaymentsSection({ clinic, date, record, enabled, kind }) {
  const k = PAYMENT_KINDS[kind];
  const { data, isLoading, isError } = useInsuranceEftDay({ clinic, date }, { enabled });

  const rows = useMemo(
    () =>
      (data?.scans || [])
        .filter((scan) => scan[k.countedKey])
        .flatMap((scan) =>
          (scan[k.rowsKey] || []).map((payment, i) => ({
            key: `${scan.report_file_id}-${i}`,
            filename: scan.original_filename,
            needsReview: scan[k.statusKey] === "needs_review",
            reviewReason: scan[k.reviewKey],
            ...payment,
          })),
        ),
    [data?.scans, k],
  );
  const pendingCount = (data?.scans || []).filter((scan) =>
    EFT_IN_PROGRESS_STATUSES.has(scan.status),
  ).length;

  // Only counted files (clean deposit-slip reads) are listed, so this total is
  // the same number the table cell and the Collection Breakdown suggestion use.
  const notCountedCount = (data?.scans || []).filter(
    (scan) => !scan[k.countedKey] && scan[k.totalKey] != null,
  ).length;
  const eftTotal = roundTo2(rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0));
  const entered = roundTo2(Number(record?.[k.recordField]) || 0);
  const { status } = readAmountMatch(
    record,
    rows.length > 0 ? { [k.dayTotalKey]: eftTotal } : null,
    kind,
  );
  const totalClass =
    status === "match"
      ? "bg-palette-emerald-muted text-palette-emerald-primary"
      : status === "mismatch"
        ? "bg-palette-rose-muted text-palette-rose-primary"
        : "bg-palette-gray-soft";

  let emptyMessage = null;
  if (isLoading) emptyMessage = "Loading…";
  else if (isError) emptyMessage = `Unable to load ${k.noun}.`;
  else if (rows.length === 0 && pendingCount > 0) emptyMessage = "Files are still being read.";
  else if (rows.length === 0) emptyMessage = `No ${k.noun} in this day's uploaded files.`;

  return (
    <section className="mt-6">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-palette-gray-primary">{k.title}</h3>
        <div className="flex items-center gap-3 text-xs text-palette-gray-secondary">
          {pendingCount > 0 && rows.length > 0 && (
            <span>
              {pendingCount} file{pendingCount === 1 ? "" : "s"} still being read
            </span>
          )}
          {notCountedCount > 0 && (
            <span title="Insurer statements repeat money already on the deposit slip, and unclear reads need checking">
              {notCountedCount} file{notCountedCount === 1 ? "" : "s"} not counted (statements or
              need review)
            </span>
          )}
        </div>
      </div>
      {emptyMessage ? (
        <p className="text-sm text-palette-gray-secondary italic">{emptyMessage}</p>
      ) : (
        <div className="overflow-auto border border-palette-gray-divider rounded max-h-[28rem]">
          <table className="min-w-full text-sm">
            <thead className="bg-palette-gray-soft text-palette-gray-heading sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Payer</th>
                <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Reference</th>
                <th className="px-3 py-2 text-left font-medium">File</th>
                <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-palette-gray-subtle">
                  <td className="px-3 py-2 break-words">{r.payer || "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums">{r.reference || "—"}</td>
                  <td className="px-3 py-2 break-words text-palette-gray-secondary">
                    {r.filename}
                    {r.needsReview && (
                      <span
                        className="ml-1 text-xs text-palette-rose-badge"
                        title={r.reviewReason || undefined}
                      >
                        · check file
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                    {formatMoney(Number(r.amount) || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={`font-semibold ${totalClass}`}>
              <tr className="border-t border-palette-gray-divider">
                <td className="px-3 py-2" colSpan={3}>
                  <div className="flex flex-col">
                    <span>Total</span>
                    <span className="text-xs font-normal text-palette-gray-badge">
                      {k.enteredLabel}: <span className="tabular-nums">{formatMoney(entered)}</span>
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                  {formatMoney(eftTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </section>
  );
}

function NetsuiteMatchModal({ isOpen, onClose, clinic, info }) {
  const tx = info?.match?.tx;
  const journalLines = Array.isArray(tx?.lines) ? tx.lines : [];
  const journalLink = tx?.link ?? "";
  const journalNumber = tx?.transaction_id ?? "";
  const hasJournal = !!info?.match;

  const depositMatch = info?.depositMatch;
  const hasDeposit = !!depositMatch;
  const depositLines = useMemo(
    () => (Array.isArray(depositMatch?.lines) ? depositMatch.lines : []),
    [depositMatch],
  );

  // Collapse deposit lines that share the same memo into a single row:
  // sum their amounts, collect all transaction links, and re-evaluate
  // match status against the line's `target` (with refund tolerance
  // pulled from the record).
  const depositGroups = useMemo(() => {
    if (depositLines.length === 0) return [];
    const refund = roundTo2(Number(info?.record?.refund) || 0);
    const byKey = new Map();
    depositLines.forEach((ln) => {
      const memo = (ln?.memo || "").trim();
      const key = memo.toLowerCase() || `__line_${ln?.line_id ?? Math.random()}`;
      let g = byKey.get(key);
      if (!g) {
        g = {
          key,
          memo: memo || "—",
          kind: ln.kind || "other",
          target: ln.target ?? null,
          amount: 0,
          line_count: 0,
          txs: [],
          _seenTxIds: new Set(),
        };
        byKey.set(key, g);
      }
      g.amount = roundTo2(g.amount + (Number(ln?.amount) || 0));
      g.line_count += 1;
      const tid = ln?.transaction_id;
      if (tid !== undefined && tid !== null && !g._seenTxIds.has(tid)) {
        g._seenTxIds.add(tid);
        g.txs.push({ id: tid, link: ln?.link || "" });
      }
    });
    return Array.from(byKey.values()).map((g) => {
      let status = "neutral";
      if (g.target !== null && g.target !== undefined && g.amount !== 0 && g.target !== 0) {
        if (
          g.amount === g.target ||
          (refund !== 0 &&
            (roundTo2(g.target + refund) === g.amount || roundTo2(g.target - refund) === g.amount))
        ) {
          status = "match";
        } else {
          status = "mismatch";
        }
      }
      return { ...g, status };
    });
  }, [depositLines, info?.record?.refund]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span>
          NetSuite match{" "}
          <span className="text-sm font-normal text-palette-gray-secondary">
            {clinic} — {info?.date}
          </span>
        </span>
      }
      size="xl"
      footer={
        <div className="flex justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {/* Journal section */}
      <section className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-palette-gray-primary">
            Journal{" "}
            {journalNumber &&
              (journalLink ? (
                <a
                  href={journalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-normal tabular-nums text-palette-emerald-hover hover:text-palette-emerald-text hover:underline"
                >
                  #{journalNumber}
                </a>
              ) : (
                <span className="font-normal text-palette-gray-secondary tabular-nums">
                  #{journalNumber}
                </span>
              ))}
          </h3>
        </div>
        {hasJournal ? (
          <div className="overflow-auto border border-palette-gray-divider rounded max-h-[20rem]">
            <table className="min-w-full text-sm">
              <thead className="bg-palette-gray-soft text-palette-gray-heading sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Date</th>
                  <th className="px-3 py-2 text-left font-medium">Memo</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Kind</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Amount</th>
                </tr>
              </thead>
              <tbody>
                {journalLines.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-palette-gray-secondary">
                      No line details available.
                    </td>
                  </tr>
                ) : (
                  journalLines.map((ln, i) => {
                    // Map this line's kind -> the matching column flag so we
                    // can tint the row the same way as the deposit table.
                    const matchKey = MATCH_KEYS.find((k) => k.tx === ln.kind);
                    const flag = matchKey ? info?.match?.flags?.[matchKey.col] : null;
                    const rowClass =
                      flag === "match"
                        ? "bg-palette-emerald-soft hover:bg-palette-emerald-muted"
                        : flag === "mismatch"
                          ? "bg-palette-rose-soft hover:bg-palette-rose-muted"
                          : "";
                    return (
                      <tr
                        key={ln.line_id ?? i}
                        className={`border-t border-palette-gray-subtle ${rowClass}`}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">{ln.trandate || "—"}</td>
                        <td className="px-3 py-2 break-words">{ln.memo || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap capitalize">
                          {ln.kind || "other"}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          {formatMoney(Number(ln.amount) || 0)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {journalLines.length > 0 && (
                <tfoot className="bg-palette-gray-soft font-semibold">
                  <tr className="border-t border-palette-gray-divider">
                    <td className="px-3 py-2" colSpan={3}>
                      Total
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                      {formatMoney(
                        journalLines.reduce((sum, ln) => sum + (Number(ln.amount) || 0), 0),
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : (
          <p className="text-sm text-palette-gray-secondary italic">
            No journal transaction matched this day.
          </p>
        )}
      </section>

      {/* Deposit section */}
      <section>
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-palette-gray-primary">Deposit</h3>
        </div>
        {hasDeposit ? (
          <div className="overflow-auto border border-palette-gray-divider rounded max-h-[28rem]">
            <table className="min-w-full text-sm">
              <thead className="bg-palette-gray-soft text-palette-gray-heading sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Tx #</th>
                  <th className="px-3 py-2 text-left font-medium">Memo</th>
                  <th className="px-3 py-2 text-left font-medium whitespace-nowrap">Kind</th>
                  <th className="px-3 py-2 text-right font-medium whitespace-nowrap">Amount</th>
                </tr>
              </thead>
              <tbody>
                {depositGroups.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-center text-palette-gray-secondary">
                      No deposit lines.
                    </td>
                  </tr>
                ) : (
                  depositGroups.map((g) => {
                    const rowClass =
                      g.status === "match"
                        ? "bg-palette-emerald-soft hover:bg-palette-emerald-muted"
                        : g.status === "mismatch"
                          ? "bg-palette-rose-soft hover:bg-palette-rose-muted"
                          : "";
                    return (
                      <tr key={g.key} className={`border-t border-palette-gray-subtle ${rowClass}`}>
                        <td className="px-3 py-2 whitespace-nowrap tabular-nums">
                          {g.txs.length === 0 ? (
                            "—"
                          ) : (
                            <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                              {g.txs.map((t) =>
                                t.link ? (
                                  <a
                                    key={t.id}
                                    href={t.link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-palette-emerald-hover hover:text-palette-emerald-text hover:underline font-medium"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {t.id}
                                  </a>
                                ) : (
                                  <span key={t.id}>{t.id}</span>
                                ),
                              )}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 break-words">
                          {g.memo}
                          {g.line_count > 1 && (
                            <span className="ml-1 text-xs text-palette-gray-secondary">
                              ×{g.line_count}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap capitalize">
                          {g.kind || "other"}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          {formatMoney(Number(g.amount) || 0)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              {depositGroups.length > 0 &&
                (() => {
                  const depositTotal = roundTo2(
                    depositGroups.reduce((sum, g) => sum + (Number(g.amount) || 0), 0),
                  );
                  const rowTotal = roundTo2(Number(info?.record?.collection_total) || 0);
                  const rowCash = roundTo2(Number(info?.record?.collection_cash) || 0);
                  const expected = roundTo2(rowTotal - rowCash);
                  const refund = roundTo2(Number(info?.record?.refund) || 0);
                  let status = "neutral";
                  if (depositTotal !== 0 && expected !== 0) {
                    if (
                      depositTotal === expected ||
                      (refund !== 0 &&
                        (roundTo2(expected + refund) === depositTotal ||
                          roundTo2(expected - refund) === depositTotal))
                    ) {
                      status = "match";
                    } else {
                      status = "mismatch";
                    }
                  }
                  const totalClass =
                    status === "match"
                      ? "bg-palette-emerald-muted text-palette-emerald-primary"
                      : status === "mismatch"
                        ? "bg-palette-rose-muted text-palette-rose-primary"
                        : "bg-palette-gray-soft";
                  return (
                    <tfoot className={`font-semibold ${totalClass}`}>
                      <tr className="border-t border-palette-gray-divider">
                        <td className="px-3 py-2" colSpan={3}>
                          <div className="flex flex-col">
                            <span>Total</span>
                            <span className="text-xs font-normal text-palette-gray-badge">
                              Expected (row total − cash):{" "}
                              <span className="tabular-nums">{formatMoney(expected)}</span>
                              {refund !== 0 && (
                                <>
                                  {" "}
                                  <span className="text-palette-gray-placeholder">
                                    (± refund {formatMoney(refund)})
                                  </span>
                                </>
                              )}
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap tabular-nums">
                          {formatMoney(depositTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  );
                })()}
            </table>
          </div>
        ) : (
          <p className="text-sm text-palette-gray-secondary italic">
            No deposit transaction matched this day.
          </p>
        )}
      </section>

      <InsurancePaymentsSection
        clinic={clinic}
        date={info?.date}
        record={info?.record}
        enabled={isOpen && Boolean(info?.date)}
        kind="eft"
      />
      <InsurancePaymentsSection
        clinic={clinic}
        date={info?.date}
        record={info?.record}
        enabled={isOpen && Boolean(info?.date)}
        kind="cheque"
      />
    </Modal>
  );
}

/**
 * DepositSheet — admin tab that renders the per-day Collection Breakdown for
 * one or more clinics in the chosen month(s). Month / area-manager / clinic
 * StatCards are provided by the parent (`Admin.jsx`). Pass `months` (array) for
 * several months, or `month` for one.
 */
function LegendSwatch({ className, children }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`inline-block h-3 w-5 rounded-sm ${className}`} />
      {children}
    </span>
  );
}

/**
 * Which reconciliation colours the cells, and what the colours mean. Without
 * `onChange` (no access to bank data) only the NetSuite & PDFs legend shows.
 */
function ColorByLegend({ colorBy, onChange }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-palette-gray-heading">
      {onChange && (
        <span className="inline-flex items-center gap-2">
          <span className="font-medium">Colour cells by:</span>
          <SegmentedControl
            options={COLOR_BY_OPTIONS}
            value={colorBy}
            onChange={onChange}
            className="text-xs"
          />
        </span>
      )}
      {colorBy === "bank" ? (
        <>
          <LegendSwatch className="bg-palette-sky-muted">Matched to bank</LegendSwatch>
          <LegendSwatch className="bg-palette-amber-muted">
            Not matched to bank yet / changed since
          </LegendSwatch>
          <span className="text-palette-gray-secondary">
            Accepted matches from Daily Deposits. Cash, Refund and Other don’t go to the bank.
          </span>
        </>
      ) : (
        <>
          <LegendSwatch className="bg-palette-emerald-muted">Matches NetSuite / PDFs</LegendSwatch>
          <LegendSwatch className="bg-palette-rose-muted">Doesn’t match</LegendSwatch>
        </>
      )}
    </div>
  );
}

export default function DepositSheet({ month, months, clinics = [] }) {
  const { user } = useAuth();
  const canSeeBank = canViewDailyBankBalance(user);
  const [colorByPicked, setColorBy] = usePersistedState("depositSheet:colorBy", "netsuite");
  const colorBy = canSeeBank ? colorByPicked : "netsuite";
  const tableColumns = visibleColumns(canSeeBank);
  const monthList = useMemo(() => {
    const list = Array.isArray(months) ? months : month ? [month] : [];
    return Array.from(new Set(list.filter(Boolean))).sort();
  }, [month, months]);
  const monthRanges = useMemo(() => monthList.map(getMonthRange).filter(Boolean), [monthList]);
  // Spans first through last selected month. `months` keeps the individual
  // months so a gap (e.g. Jan + Mar) doesn't render February's days.
  const monthRange = useMemo(() => {
    if (monthRanges.length === 0) return null;
    return {
      from: monthRanges[0].from,
      to: monthRanges[monthRanges.length - 1].to,
      months: monthRanges,
      label: monthRanges.map((r) => `${r.year}-${pad2(r.month)}`).join(", "),
    };
  }, [monthRanges]);
  const isInSelectedMonths = useCallback(
    (iso) => monthRanges.some((r) => iso >= r.from && iso <= r.to),
    [monthRanges],
  );
  const uniqueClinics = useMemo(
    () => Array.from(new Set(clinics.filter(Boolean))).map((c) => String(c).toUpperCase()),
    [clinics],
  );

  // Optional specific-day filter (must fall within the selected month).
  const [selectedDay, setSelectedDay] = useState("");
  useEffect(() => {
    if (!monthRange) {
      setSelectedDay("");
      return;
    }
    if (selectedDay && !isInSelectedMonths(selectedDay)) {
      setSelectedDay("");
    }
  }, [monthRange, selectedDay, isInSelectedMonths]);

  // Effective date range — narrows to a single day when one is picked.
  const effectiveRange = useMemo(() => {
    if (!monthRange) return null;
    if (selectedDay) {
      const dayMonth = monthRanges.find((r) => selectedDay >= r.from && selectedDay <= r.to);
      if (dayMonth) {
        return {
          ...monthRange,
          from: selectedDay,
          to: selectedDay,
          months: [{ ...dayMonth, from: selectedDay, to: selectedDay }],
        };
      }
    }
    return monthRange;
  }, [monthRange, monthRanges, selectedDay]);

  // One cached request per selected month — adding a month only fetches that
  // month, and switching the day filter never re-fetches (sliced client-side).
  const { data, isLoading } = useMultiClinicCollectionBreakdownsForMonths(
    uniqueClinics,
    monthRanges,
    { enabled: uniqueClinics.length > 0 },
  );

  // Resolve clinic codes → NetSuite subsidiary ids, then fetch monthly
  // transactionLine debits for those subsidiaries (grouped by transaction).
  const { data: clinicsData, isFetching: isFetchingClinics } = useClinicSubsidiaryMap({
    enabled: uniqueClinics.length > 0,
  });
  const subsidiaryIds = useMemo(() => {
    const profiles = clinicsData?.results || [];
    const wanted = new Set(uniqueClinics);
    const ids = new Set();
    profiles.forEach((p) => {
      const code = String(p?.clinic || "").toUpperCase();
      if (wanted.has(code) && p?.netsuite_subsidiary_id) {
        ids.add(p.netsuite_subsidiary_id);
      }
    });
    return Array.from(ids);
  }, [clinicsData, uniqueClinics]);

  // Every subsidiary this user can see — fixes the NetSuite batch boundaries so
  // changing the clinic selection reuses cached batches instead of refetching.
  const allSubsidiaryIds = useMemo(
    () =>
      (clinicsData?.results || []).map((p) => p?.netsuite_subsidiary_id).filter((id) => id != null),
    [clinicsData],
  );

  // Map clinic code -> netsuite_subsidiary_id (for matching tx rows below).
  const subsidiaryByClinic = useMemo(() => {
    const profiles = clinicsData?.results || [];
    const map = {};
    profiles.forEach((p) => {
      const code = String(p?.clinic || "").toUpperCase();
      if (code && p?.netsuite_subsidiary_id) {
        map[code] = p.netsuite_subsidiary_id;
      }
    });
    return map;
  }, [clinicsData]);

  // NetSuite searches take one month each — one cached request per month, merged.
  const { data: txLinesData, isFetching: isFetchingTxLines } =
    useSearchNetsuiteTransactionLinesForMonths(
      { subsidiaries: subsidiaryIds, allSubsidiaries: allSubsidiaryIds, months: monthList },
      { enabled: subsidiaryIds.length > 0 },
    );

  // Insurance EFT totals read from uploaded files, per clinic-day, for the whole
  // month in one request (drives the Insurance EFT cell tint).
  const { data: eftTotalsData } = useMultiClinicInsuranceEftTotalsForMonths(
    uniqueClinics,
    monthRanges,
  );

  const { data: depositData, isFetching: isFetchingDeposits } = useSearchNetsuiteDepositsForMonths(
    { subsidiaries: subsidiaryIds, allSubsidiaries: allSubsidiaryIds, months: monthList },
    { enabled: subsidiaryIds.length > 0 },
  );

  // Group NetSuite transaction rows by their subsidiary id, so each
  // ClinicDepositCard receives only its own candidates. Each tx is
  // enriched with per-card-type totals (visa/mastercard/amex/debit/other)
  // derived from its lines via `classifyMemo` — backend only returns raw
  // lines so memo regexes live in one place (`memoFormats.js`).
  const txBySubsidiary = useMemo(() => {
    const items = txLinesData?.items || [];
    const map = {};
    items.forEach((tx) => {
      const sid = tx?.subsidiary_id;
      if (sid === undefined || sid === null) return;
      if (!map[sid]) map[sid] = [];
      map[sid].push(enrichTxWithCardBuckets(tx));
    });
    return map;
  }, [txLinesData]);

  // Map subsidiary_id -> deposit-group { subsidiary_id, total, line_count, dates: [...] }.
  // Each line is normalized so the rest of the UI can use `ln.amount` / `ln.kind`
  // (backend returns `debitforeignamount`; memo classification lives in
  // `memoFormats.js`).
  const depositBySubsidiary = useMemo(() => {
    const items = depositData?.items || [];
    const map = {};
    items.forEach((item) => {
      const sid = item?.subsidiary_id;
      if (sid === undefined || sid === null) return;
      const dates = (item?.dates || []).map((d) => ({
        ...d,
        lines: (d?.lines || []).map((ln) => ({
          ...ln,
          amount: Number(ln?.debitforeignamount ?? ln?.amount) || 0,
          kind: classifyMemo(ln?.memo),
        })),
      }));
      map[sid] = { ...item, dates };
    });
    return map;
  }, [depositData]);
  // The request spans first→last month, so drop days in unselected gap months.
  const resultsByClinic = useMemo(() => {
    const all = data?.results || {};
    const keep = selectedDay ? (d) => d === selectedDay : isInSelectedMonths;
    const filtered = {};
    for (const [clinic, rows] of Object.entries(all)) {
      filtered[clinic] = (rows || []).filter((r) => r?.date && keep(r.date));
    }
    return filtered;
  }, [data, selectedDay, isInSelectedMonths]);

  // Grand totals across all selected clinics, per column.
  // Cross-clinic sums of the RECORD-backed fields only. The reconciliation trio is
  // excluded: this card has no NetSuite deposit context, so a "NetSuite Total"
  // here could only ever be a misleading zero. `collection_total` is summed
  // explicitly — it drives the headline figure below and is no longer a column.
  const grandTotals = useMemo(() => {
    const keys = [
      ...MONTHLY_TABLE_COLUMNS.filter((c) => !DERIVED_KEYS.has(c.key)).map((c) => c.key),
      ...TOTAL_ONLY_KEYS,
    ];
    const totals = {};
    keys.forEach((key) => {
      totals[key] = 0;
    });
    uniqueClinics.forEach((c) => {
      (resultsByClinic[c] || []).forEach((row) => {
        keys.forEach((key) => {
          const n = Number(row?.[key]);
          if (Number.isFinite(n)) totals[key] += n;
        });
      });
    });
    return totals;
  }, [resultsByClinic, uniqueClinics]);

  // Deposit lines Accepted against the bank on Daily Deposits, every selected
  // clinic, first through last selected month.
  const bankMatches = useDepositMatchesForClinics(
    uniqueClinics,
    { date_from: monthRange?.from, date_to: monthRange?.to },
    { enabled: canSeeBank },
  );
  const bankByRowKey = useMemo(
    () => indexBankMatches(bankMatches.byClinic),
    [bankMatches.byClinic],
  );

  // Table rows, one per clinic-day. Each clinic's deposit lines are indexed by
  // `trandate` once, then every row picks out its own day.
  const rowsByClinic = useMemo(() => {
    if (!effectiveRange) return {};
    const out = {};
    uniqueClinics.forEach((c) => {
      const sid = subsidiaryByClinic[c];
      const transactions = txBySubsidiary[sid] || [];
      // Intercompany lines are split off here, so the NetSuite match modal, the
      // card matching and NetSuite Total never see them; they get their own column.
      const linesByDate = {};
      const intercompanyByDate = {};
      (depositBySubsidiary[sid]?.dates || []).forEach((d) => {
        if (!d?.trandate) return;
        const lines = d.lines || [];
        linesByDate[d.trandate] = lines.filter((ln) => ln.kind !== "intercompany");
        intercompanyByDate[d.trandate] = lines.filter((ln) => ln.kind === "intercompany");
      });
      const recordByDate = new Map();
      (resultsByClinic[c] || []).forEach((r) => r?.date && recordByDate.set(r.date, r));
      const eftByDate = eftTotalsData?.results?.[c] || EMPTY_EFT_TOTALS;
      const rows = [];
      effectiveRange.months.forEach(({ year, month: m, from, to }) => {
        for (let d = Number(from.slice(-2)); d <= Number(to.slice(-2)); d++) {
          const iso = `${year}-${pad2(m)}-${pad2(d)}`;
          rows.push({
            key: `${c}|${iso}`,
            clinic: c,
            date: iso,
            record: recordByDate.get(iso) || null,
            transactions,
            depositLines: linesByDate[iso] || null,
            intercompanyLines: intercompanyByDate[iso] || null,
            eftTotals: eftByDate[iso],
            bankByField: bankByRowKey[`${c}|${iso}`] || null,
          });
        }
      });
      out[c] = rows;
    });
    return out;
  }, [
    effectiveRange,
    uniqueClinics,
    subsidiaryByClinic,
    txBySubsidiary,
    depositBySubsidiary,
    resultsByClinic,
    eftTotalsData,
    bankByRowKey,
  ]);

  // With a day picked, every clinic's single row goes into one table.
  const dayRows = useMemo(
    () => (selectedDay ? uniqueClinics.flatMap((c) => rowsByClinic[c] || []) : []),
    [selectedDay, uniqueClinics, rowsByClinic],
  );

  // Registry for "Download CSV — All Clinics" (see handleDownloadAllCsv).
  const csvExportersRef = useRef(new Map());
  const registerCsvExport = useCallback((key, build) => {
    csvExportersRef.current.set(key, build);
    return () => csvExportersRef.current.delete(key);
  }, []);

  if (!monthRange) {
    return (
      <Card className="mt-8">
        <p className="text-sm text-palette-gray-badge">Select a month to view the deposit sheet.</p>
      </Card>
    );
  }

  if (uniqueClinics.length === 0) {
    return (
      <Card className="mt-8">
        <p className="text-sm text-palette-gray-badge">
          Select one or more clinics to view deposit sheets.
        </p>
      </Card>
    );
  }

  // "Download CSV — All Clinics": each clinic's table registers a builder for
  // its own rows, so the file carries the figures those tables compute (derived
  // Deposit Total / NetSuite Total / Difference included) without a second copy
  // of that logic. One block per clinic in page order, each ending in its Total,
  // then a grand total — every column is a plain sum, so the clinic totals add.
  function handleDownloadAllCsv() {
    const header = ["Clinic", "Day", ...tableColumns.map((c) => c.label)];
    const lines = [header.map(csvCell).join(",")];
    const grand = tableColumns.map(() => 0);
    uniqueClinics.forEach((c) => {
      const built = csvExportersRef.current.get(c)?.();
      if (!built || built.rows.length === 0) return;
      built.rows.forEach((row) => lines.push([c, ...row].map(csvCell).join(",")));
      lines.push([c, "Total", ...built.totals].map(csvCell).join(","));
      built.totals.forEach((v, i) => {
        grand[i] += Number(v) || 0;
      });
    });
    lines.push(["All Clinics", "Total", ...grand.map(roundTo2)].map(csvCell).join(","));
    downloadCsvLines(
      lines,
      `deposit-sheet_all-clinics_${monthRange.label.replace(/, /g, "_")}.csv`,
    );
  }

  return (
    <div>
      <Card className="mt-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-lg font-semibold">
              Totals — {uniqueClinics.length} clinic{uniqueClinics.length === 1 ? "" : "s"}
            </h3>
            <span className="text-lg font-bold tabular-nums text-palette-emerald-hover">
              {isLoading ? "…" : formatMoney(grandTotals.collection_total || 0)}
            </span>
            <div className="relative group">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-palette-gray-divider bg-palette-gray-soft px-2.5 py-1 text-xs font-medium text-palette-gray-heading hover:bg-palette-gray-subtle"
              >
                View totals
                <span aria-hidden="true">▾</span>
              </button>
              <div className="absolute left-0 top-full mt-1 z-20 hidden group-hover:block group-focus-within:block">
                <dl className="w-72 rounded-lg border border-palette-gray-divider bg-white shadow-lg divide-y divide-palette-gray-subtle">
                  {/* Derived columns are skipped — see `grandTotals`. The Total row
                      is appended after the list instead of appearing in it. */}
                  {MONTHLY_TABLE_COLUMNS.filter((c) => !DERIVED_KEYS.has(c.key)).map((c) => {
                    const color = COLUMN_COLORS[c.key] || COLUMN_COLORS.collection_other;
                    return (
                      <div
                        key={c.key}
                        className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs text-palette-gray-heading"
                      >
                        <dt className="flex items-center gap-2 min-w-0">
                          <span
                            className={`inline-block h-2 w-2 rounded-full flex-shrink-0 ${color.badge}`}
                          />
                          <span className="truncate">{c.label}</span>
                        </dt>
                        <dd className="tabular-nums whitespace-nowrap">
                          {isLoading ? (
                            <span className="text-palette-gray-placeholder">…</span>
                          ) : (
                            formatMoney(grandTotals[c.key] || 0)
                          )}
                        </dd>
                      </div>
                    );
                  })}
                  <div className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs bg-palette-gray-soft font-semibold text-palette-gray-primary">
                    <dt className="flex items-center gap-2 min-w-0">
                      <span className="inline-block h-2 w-2 rounded-full flex-shrink-0 bg-palette-gray-badge" />
                      <span className="truncate">Total</span>
                    </dt>
                    <dd className="tabular-nums whitespace-nowrap">
                      {isLoading ? (
                        <span className="text-palette-gray-placeholder">…</span>
                      ) : (
                        formatMoney(grandTotals.collection_total || 0)
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
          <p className="text-xs text-palette-gray-secondary mt-0.5">
            {selectedDay
              ? `Showing ${selectedDay}`
              : `${monthRange.label} (${monthRange.from} through ${monthRange.to})`}
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <label className="text-xs font-medium text-palette-gray-heading">Filter by day:</label>
            <input
              type="date"
              value={selectedDay}
              min={monthRange.from}
              max={monthRange.to}
              onChange={(e) => setSelectedDay(e.target.value)}
              className="rounded-md border border-palette-gray-border px-2 py-1 text-xs"
            />
            {selectedDay && (
              <button
                type="button"
                onClick={() => setSelectedDay("")}
                className="inline-flex items-center rounded-md border border-palette-gray-divider bg-palette-gray-soft px-2 py-1 text-xs font-medium text-palette-gray-heading hover:bg-palette-gray-subtle"
              >
                Clear
              </button>
            )}
          </div>

          <ColorByLegend colorBy={colorBy} onChange={canSeeBank ? setColorBy : null} />
        </div>
      </Card>

      {!selectedDay && uniqueClinics.length > 0 && (
        <div className="mt-6 flex justify-end">
          <Button
            type="button"
            variant="primary"
            onClick={handleDownloadAllCsv}
            disabled={isLoading}
          >
            Download CSV — All Clinics
          </Button>
        </div>
      )}

      {selectedDay ? (
        <DepositTable
          mode="clinic"
          rows={dayRows}
          isLoading={isLoading}
          title={`Deposit Sheet — ${uniqueClinics.length} clinic${
            uniqueClinics.length === 1 ? "" : "s"
          }`}
          heading={`Daily Collection Breakdown — ${selectedDay}`}
          subtitle={`Showing ${selectedDay}, one row per clinic`}
          csvName={`deposit-sheet_${selectedDay}.csv`}
          emptyText="No data for this day"
          colorBy={colorBy}
          canSeeBank={canSeeBank}
          bankLoaded={bankMatches.isSuccess}
          bankError={bankMatches.isError}
        />
      ) : (
        uniqueClinics.map((c) => (
          <DepositTable
            key={c}
            mode="day"
            rows={rowsByClinic[c] || []}
            isLoading={isLoading}
            title={
              <>
                <span className="font-bold">{c}</span>
                <span> — Deposit Sheet</span>
              </>
            }
            heading={`Daily Collection Breakdown — ${monthRange.label}`}
            subtitle={`Showing ${monthRange.from} through ${monthRange.to}`}
            csvName={`deposit-sheet_${c}_${monthRange.label.replace(/, /g, "_")}.csv`}
            onRegisterCsvExport={registerCsvExport}
            csvExportKey={c}
            emptyText={
              monthRange.months.length > 1 ? "No data for these months" : "No data for this month"
            }
            colorBy={colorBy}
            canSeeBank={canSeeBank}
            bankLoaded={bankMatches.isSuccess}
            bankError={bankMatches.isError}
          />
        ))
      )}
      {(isLoading || isFetchingClinics || isFetchingTxLines || isFetchingDeposits) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm pointer-events-none">
          <Loading
            text={
              isLoading
                ? "Loading deposit sheets..."
                : isFetchingTxLines || isFetchingDeposits
                  ? "Matching NetSuite transactions..."
                  : "Loading clinics..."
            }
            size="lg"
          />
        </div>
      )}
    </div>
  );
}
